
import { useAsyncEffect } from 'ahooks';
import { Empty, Spin, Tag, Typography } from 'antd';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import NoRecommendIcon from '@/assets/images/header/no-recommend.svg?react';
import { BASE_NAME, toSearchStr } from '@/infrastructure/history';
import { getProductId } from '@/infrastructure/product-info';
import { CommonDateRange } from '@/infrastructure/utils/timestamp';
import { IssueType } from '@/proto/common';
import { TimeType } from '@/proto/issue';
import { genStandardToken } from '@/proto/token';
import { IRecommendedIssueInfo, issueService } from '@/service/issue';
import { MatchMode } from '../form/standards/types';
import styles from './index.module.scss';

const { Paragraph } = Typography;

export const IssueRecommendList = () => {
  const {} = {};
  const [recommendIssueList, setRecommendIssueList] = useState<IRecommendedIssueInfo[]>([]);
  const [loading, setLoading] = useState(false);

  const renderTagByIssueType = (issueType: IssueType) => {
    switch (issueType) {
      case IssueType.ISSUE_TYPE_CRASH:
        return <Tag className={styles.crashTag}>崩溃</Tag>;
      case IssueType.ISSUE_TYPE_ANR_BUGLY:
        return <Tag className={styles.anrTag}>ANR</Tag>;
      default:
        return <Tag className={styles.crashTag}>崩溃</Tag>;
    }
  };

  const toExceptionPage = async (list: IRecommendedIssueInfo) => {
    const timeRange = CommonDateRange.lastDays(30);
    const token = await genStandardToken({
      time_quick: '近30天',
      time_type: 'report',
      time_window: TimeType.TIME_TYPE_DAY,
      date: 'custom',
      end_date_str: timeRange[1].format('YYYY-MM-DD HH:mm:ss'),
      start_date_str: timeRange[0].format('YYYY-MM-DD HH:mm:ss'),
      start_time: timeRange[0].unix(),
      end_time: timeRange[1].unix(),
      filters: [
        {
          name: 'event_time',
          operator: MatchMode.Ge,
          values: [`${timeRange[0].unix()}`],
        },
        {
          name: 'event_time',
          operator: MatchMode.Le,
          values: [`${timeRange[1].unix()}`],
        },
      ],
    });
    const queryStr = toSearchStr({
      token,
      feature: list.feature,
    });
    let url = '';
    switch (list.issueType) {
      case IssueType.ISSUE_TYPE_CRASH:
        url = `${location.origin}${BASE_NAME}/exception/crash/issues/detail?${queryStr}`;
        window.open(url);
        return;
      case IssueType.ISSUE_TYPE_ANR_BUGLY:
        url = `${location.origin}${BASE_NAME}/exception/anr/issue/detail?${queryStr}`;
        return;
      default:
        return;
    }
  };

  const renderKeyStack = (keyStack: string, displayLine?: number) => {
    const keyStacks: string[] = keyStack?.split('\n') ?? [];
    // 显示前3行
    const displayKeyStacks = displayLine ? keyStacks.slice(0, displayLine) : [...keyStacks];
    const stackNodes = [];

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < displayKeyStacks.length; i++) {
      stackNodes.push(<Paragraph
        style={{ marginBottom: 0 }}
        ellipsis={{
          rows: 1,
          expandable: false,
        }}
      >
        {displayKeyStacks[i]}
      </Paragraph>);
    }

    return stackNodes;
  };

  const renderIssueList = () => recommendIssueList.map(list => (
    <div className={styles.recommentList}>
      <div className={styles.recommentItem}>
        <div className={styles.title}>
          问题类型：{renderTagByIssueType(list.issueType as IssueType)}
        </div>
        <div className={styles.desc}>Issue ID和问题特征： </div>
        <div className={styles.feature} onClick={() => toExceptionPage(list)}>{list.feature}</div>
        <div className={styles.stack}>
          {renderKeyStack(list.keyMethod ?? '')}
        </div>
      </div>
    </div>
  ));

  useAsyncEffect(async () => {
    setLoading(true);
    const issueList = await issueService.getRecommendIssueList({
      productId: getProductId(),
      limit: 10,
      offset: 0,
    });

    setRecommendIssueList(issueList);
    setLoading(false);
  }, []);

  return (
    <Spin wrapperClassName={styles.spinWrapper} className={styles.spinWrapper} spinning={loading}>
      {renderIssueList()}
      {isEmpty(recommendIssueList) && <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}>
          <Empty
            description='暂无推荐问题'
            image={<NoRecommendIcon />}
            imageStyle={{ width: 200, height: 200 }}
          />
        </div>}
    </Spin>
  );
};
